package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Horizontal strip of a member's public photos.
 *
 * Read-only on someone else's profile; on your own, [onAdd] appends a trailing "+" tile and
 * [onRemove] puts a delete affordance on each photo. The caller enforces the cap (the Firestore rule
 * allows at most 6) and simply stops passing [onAdd] once it's reached.
 *
 * Holds no copy — every label arrives resolved (golden rule 6).
 */
@Composable
fun ProfilePhotoStrip(
    photos: List<String>,
    photoContentDescription: String,
    modifier: Modifier = Modifier,
    onPhotoClick: ((index: Int) -> Unit)? = null,
    addLabel: String? = null,
    onAdd: (() -> Unit)? = null,
    removeContentDescription: String? = null,
    onRemove: ((url: String) -> Unit)? = null,
) {
    if (photos.isEmpty() && onAdd == null) return
    LazyRow(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        contentPadding = PaddingValues(end = AppTheme.spacing.lg),
    ) {
        items(photos, key = { it }) { url ->
            Box(
                modifier = Modifier
                    .width(AppTheme.sizes.profilePhotoTile)
                    .aspectRatio(1f)
                    .clip(AppTheme.shapes.card)
                    .background(AppTheme.colors.surfaceContainerHigh),
            ) {
                val index = photos.indexOf(url)
                BusinessCover(
                    imageUrl = url,
                    contentDescription = photoContentDescription,
                    iconName = "photo_camera",
                    modifier = if (onPhotoClick != null) {
                        Modifier.clickable { onPhotoClick(index) }
                    } else {
                        Modifier
                    },
                )
                if (onRemove != null && removeContentDescription != null) {
                    // Scrim behind the glyph so it stays legible over a bright photo.
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(AppTheme.spacing.xs)
                            .size(AppTheme.sizes.minTouchTarget / 2)
                            .clip(AppTheme.shapes.pill)
                            .background(AppTheme.colors.scrim.copy(alpha = 0.55f)),
                        contentAlignment = Alignment.Center,
                    ) {
                        AppIconButton(
                            iconName = "close",
                            contentDescription = removeContentDescription,
                            onClick = { onRemove(url) },
                            tint = AppTheme.colors.onScrim,
                        )
                    }
                }
            }
        }
        if (onAdd != null && addLabel != null) {
            item(key = "add") {
                Box(
                    modifier = Modifier
                        .width(AppTheme.sizes.profilePhotoTile)
                        .aspectRatio(1f)
                        .clip(AppTheme.shapes.card)
                        .background(AppTheme.colors.surfaceContainerHigh)
                        .clickable(onClick = onAdd),
                    contentAlignment = Alignment.Center,
                ) {
                    AppIcon(
                        name = "add_a_photo",
                        contentDescription = addLabel,
                        size = AppTheme.sizes.iconLg,
                        tint = AppTheme.colors.primary,
                    )
                }
            }
        }
    }
}
